/**
 * <engraving-picker> - engraving popup of the product page (see snippets/engraving.liquid)
 *
 * The popup works on a draft: it is filled from the saved engraving each time it opens, and the draft only replaces
 * the saved engraving when "Save" is clicked. Saved values are written to hidden inputs linked to the product form.
 *
 * Icons are stored in the text as tokens, such as "Amy [Heart] Jack". A token counts as one character, is deleted
 * at once with backspace and is drawn as the icon in the preview. The order shows the token text.
 */

// English letters, digits, spaces and common punctuation. Everything else, such as emoji, is removed
const DISALLOWED_CHARACTERS = /[^A-Za-z0-9 .,'’&\-!?:/+#@()"*]/g;

// Letters that don't lose their accent by Unicode decomposition
const ENGLISH_LETTERS = { 'ı': 'i', 'İ': 'I', 'ß': 'ss', 'æ': 'ae', 'Æ': 'AE', 'œ': 'oe', 'Œ': 'OE', 'ø': 'o', 'Ø': 'O', 'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D' };

// Turns accented and Turkish letters into English ones: İ → I, ş → s, é → e...
const toEnglishLetters = (text) =>
  text
    .replace(/[ıİßæÆœŒøØłŁđĐ]/g, (letter) => ENGLISH_LETTERS[letter])
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
const TOKEN_SPLIT = /(\[[^[\]]*\])/;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
// In the preview an icon takes the place of an em space. Next to a letter a thin space is added on that side, so
// slanted script letters don't run into the icon. The icon is drawn on the em space at this share of the font size.
const ICON_SPACE = '\u2003';
const ICON_LETTER_GAP = '\u2009';
const ICON_SCALE = 0.78;

class EngravingPicker extends HTMLElement {
  connectedCallback() {
    if (this.modal) return;

    this.modal = this.querySelector('x-modal');
    if (!this.modal) return;

    this.saved = { surface: null, inside: '', outside: '', font: null };
    this.lastValues = {};
    this.activeField = null;

    this.fields = Array.from(this.modal.querySelectorAll('[data-engraving-field]'));
    this.surfaceRadios = Array.from(this.modal.querySelectorAll('[data-engraving-surface]'));
    this.fontRadios = Array.from(this.modal.querySelectorAll('[data-engraving-font]'));
    this.saveButton = this.modal.querySelector('[data-engraving-save]');

    const iconButtons = Array.from(this.modal.querySelectorAll('[data-engraving-icon]'));
    this.icons = new Map(iconButtons.map((button) => [button.dataset.engravingIcon, button.dataset.iconUrl]));
    this.iconMasks = new Map(iconButtons.map((button) => [button.dataset.engravingIcon, button.dataset.iconMask]));
    // When set, the inside engraving always uses this font; the font list only applies to the outside engraving
    this.insideFontFamily = this.getAttribute('inside-font-family');

    this.drawingRing = this.modal.querySelector('[data-engraving-ring="drawing"]');
    this.photoRing = this.modal.querySelector('[data-engraving-ring="photo"]');
    this.activeRing = this.drawingRing;

    if (this.fontRadios.length > 0 && !this.fontRadios.some((radio) => radio.checked)) {
      this.fontRadios[0].checked = true;
    }

    this.defaults = {
      surface: this.surfaceRadios.find((radio) => radio.checked)?.value ?? null,
      font: this.fontRadios.find((radio) => radio.checked)?.value ?? null
    };

    this.saved.surface = this.defaults.surface;
    this.saved.font = this.defaults.font;

    // The modal is moved to <body> while open, so its events are listened on the modal itself
    this.modal.addEventListener('dialog:before-show', () => this.loadDraft());
    this.modal.addEventListener('input', (event) => this.onModalInput(event));
    this.modal.addEventListener('change', (event) => this.onModalChange(event));
    this.modal.addEventListener('keydown', (event) => this.onModalKeydown(event));
    this.modal.addEventListener('focusin', (event) => {
      if (event.target.matches('[data-engraving-field]')) this.activeField = event.target;
    });
    this.modal.addEventListener('click', (event) => this.onModalClick(event));

    // The font dropdown is moved to <body> on small screens, so its changes are listened on it too
    this.fontPopover = this.modal.querySelector('x-popover');
    this.fontPopover?.addEventListener('change', (event) => {
      event.stopPropagation();
      this.onModalChange(event);
    });

    this.querySelector('[data-engraving-remove]')?.addEventListener('click', () => this.remove());
    this.setupHelp();

    // Photos load before the popup opens: once the page is idle, and again (for a newly selected color) when the
    // customer heads to the button
    if (this.photoRing) {
      const addButton = this.querySelector('[data-engraving-add]');
      const idle = window.requestIdleCallback ?? ((callback) => setTimeout(callback, 1500));

      idle(() => this.preloadPhotos());
      ['pointerenter', 'touchstart', 'focus'].forEach((type) => {
        addButton?.addEventListener(type, () => this.preloadPhotos(), { passive: true });
      });
    }

    // Theme editor: open the popup when the block is selected
    if (window.Shopify?.designMode) {
      const blockElement = this.closest('[data-block-id]');
      blockElement?.addEventListener('shopify:block:select', (event) => this.modal.show(!event.detail.load));
      blockElement?.addEventListener('shopify:block:deselect', () => this.modal.hide());
    }

    this.renderSaved();
  }

  /* ---------- Draft (popup) ---------- */

  loadDraft() {
    this.surfaceRadios.forEach((radio) => (radio.checked = radio.value === this.saved.surface));
    this.fontRadios.forEach((radio) => (radio.checked = radio.value === this.saved.font));

    this.fields.forEach((field) => {
      field.value = this.saved[field.dataset.engravingField];
      this.lastValues[field.dataset.engravingField] = field.value;
    });

    this.activeField = null;
    this.clearErrors();
    this.updateRingMetal();
    this.updateDraft();
  }

  get draftSurface() {
    return this.surfaceRadios.find((radio) => radio.checked)?.value ?? this.defaults.surface;
  }

  get draftFont() {
    return this.fontRadios.find((radio) => radio.checked) ?? null;
  }

  // Fields used by the selected surface. Without a surface picker, every field is used
  isFieldActive(name) {
    const surface = this.draftSurface;
    return !surface || surface === 'both' || surface === name;
  }

  updateDraft() {
    const fontFamily = this.draftFont?.dataset.fontFamily;
    const selectedFont = this.modal.querySelector('[data-engraving-font-selected]');

    if (selectedFont && this.draftFont) {
      selectedFont.textContent = this.draftFont.value;
      selectedFont.style.fontFamily = `"${fontFamily}", serif`;
    }

    this.modal.querySelectorAll('[data-engraving-field-group]').forEach((group) => {
      group.hidden = !this.isFieldActive(group.dataset.engravingFieldGroup);
    });

    // With a fixed inside font, the font list only matters when there is an outside engraving
    if (this.insideFontFamily) {
      const fontGroup = this.modal.querySelector('[data-engraving-font-group]');
      const fontNote = this.modal.querySelector('[data-engraving-font-note]');

      if (fontGroup) fontGroup.hidden = !this.isFieldActive('outside');
      if (fontNote) fontNote.hidden = !(this.isFieldActive('outside') && this.isFieldActive('inside'));
    }

    // "Clear all" is only active when there is something to clear
    const clearButton = this.modal.querySelector('[data-engraving-clear]');

    if (clearButton) {
      clearButton.disabled =
        this.fields.every((field) => field.value === '') &&
        this.draftSurface === this.defaults.surface &&
        (this.draftFont?.value ?? null) === this.defaults.font;
    }

    this.fields.forEach((field) => {
      const name = field.dataset.engravingField;
      const counter = this.modal.querySelector(`[data-engraving-counter="${name}"]`);

      if (counter) counter.textContent = `${this.countCharacters(field.value)}/${field.dataset.maxLength}`;

      this.renderRingText(name, field.value, this.fontFamilyFor(name, fontFamily));
    });

    // Text is measured to place the icons, so it is drawn again once the font has loaded
    [fontFamily, this.insideFontFamily].forEach((family) => {
      if (family && document.fonts && !document.fonts.check(`20px "${family}"`)) {
        document.fonts.load(`20px "${family}"`).then(() => {
          if (this.modal.open) this.updateDraft();
        });
      }
    });
  }

  // The font of an engraving: the fixed inside font for the inside, the selected font otherwise
  fontFamilyFor(name, selectedFontFamily) {
    return name === 'inside' && this.insideFontFamily ? this.insideFontFamily : selectedFontFamily;
  }

  // Whether the selected font is used by an engraving with these texts
  usesSelectedFont(texts) {
    return !this.insideFontFamily || texts.outside !== '';
  }

  /* ---------- Ring preview ---------- */

  // The ring takes the color of the selected "Color" option of the product: its photo when there is one, otherwise
  // the drawing in that color
  // The metal of the selected "Color" option: yellow, rose or white
  get selectedMetal() {
    const optionName = this.getAttribute('color-option') || 'color';
    const section = this.closest('.shopify-section') || document;
    const option = section.querySelector(`.variant-picker__option[data-option-name="${CSS.escape(optionName)}"]`);
    const value = option?.querySelector('.variant-picker__option-info span')?.textContent ?? '';

    if (/rose/i.test(value)) return 'rose';
    if (/white|platinum|silver/i.test(value)) return 'white';
    return 'yellow';
  }

  photoFor(metal) {
    return this.photoRing?.dataset[`photo${metal.charAt(0).toUpperCase() + metal.slice(1)}`];
  }

  // Downloads the photo of the selected color ahead of time, so it is ready when the popup opens
  preloadPhotos() {
    const url = this.photoFor(this.selectedMetal);
    if (!url) return;

    this.preloadedPhotos ??= new Set();
    if (this.preloadedPhotos.has(url)) return;

    this.preloadedPhotos.add(url);
    new Image().src = url;
  }

  updateRingMetal() {
    if (!this.drawingRing) return;

    const metal = this.selectedMetal;

    // The photo of the color when it has one, otherwise the drawing
    const photo = this.photoFor(metal);

    if (photo) {
      this.photoRing.querySelector('[data-engraving-photo]').setAttribute('href', photo);
      this.photoRing.dataset.metal = metal;
      this.activeRing = this.photoRing;
    } else {
      this.drawingRing.dataset.metal = metal;
      this.activeRing = this.drawingRing;
    }

    this.drawingRing.hidden = this.activeRing !== this.drawingRing;
    if (this.photoRing) this.photoRing.hidden = this.activeRing !== this.photoRing;
  }

  // Writes the text along the curve of its surface and draws the icons on it
  renderRingText(name, value, fontFamily) {
    const ring = this.activeRing ?? this.modal;
    const textPath = ring.querySelector(`[data-engraving-preview="${name}"]`);
    if (!textPath) return;

    const text = textPath.closest('text');
    const side = textPath.closest('[data-engraving-ring-side]');
    const iconLayer = ring.querySelector(`[data-engraving-ring-icons="${name}"]`);
    const isActive = this.isFieldActive(name);

    side.classList.toggle('is-hidden', !isActive);
    text.style.fontFamily = fontFamily ? `"${fontFamily}", serif` : '';
    textPath.replaceChildren();
    iconLayer.replaceChildren();

    // Nothing is shown until the customer types; then the text appears as it is typed
    if (value.trim() !== '') {
      // Each icon takes the place of a blank slot, measured afterwards to draw the icon at the same spot
      const parts = value.split(TOKEN_SPLIT).filter((part) => part !== '');
      const isLetterSide = (part, pattern) => part !== undefined && !this.isToken(part) && pattern.test(part);

      parts.forEach((part, index) => {
        if (this.isToken(part)) {
          const gapBefore = isLetterSide(parts[index - 1], /\S$/) ? ICON_LETTER_GAP : '';
          const gapAfter = isLetterSide(parts[index + 1], /^\S/) ? ICON_LETTER_GAP : '';
          const slot = document.createElementNS(SVG_NAMESPACE, 'tspan');
          slot.dataset.icon = part.slice(1, -1);
          slot.dataset.iconAt = gapBefore.length;
          slot.textContent = gapBefore + ICON_SPACE + gapAfter;
          textPath.append(slot);
        } else {
          textPath.append(part);
        }
      });
    }

    if (!isActive || !this.modal.open) return;

    // Long texts get smaller to stay on the visible part of the curve
    const baseSize = Number(text.dataset.size);
    const maxLength = Number(text.dataset.maxLength);
    text.style.fontSize = `${baseSize}px`;

    try {
      const length = text.getComputedTextLength();
      if (length > maxLength) {
        text.style.fontSize = `${Math.max(baseSize * 0.45, (baseSize * maxLength) / length)}px`;
      }

      const inkCenter = this.centerOnPath(text, textPath);
      const iconSize = parseFloat(text.style.fontSize) * ICON_SCALE;
      let index = 0;

      textPath.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.dataset.icon && this.iconMasks.has(node.dataset.icon)) {
          const slotIndex = index + Number(node.dataset.iconAt);
          const start = text.getStartPositionOfChar(slotIndex);
          const end = text.getEndPositionOfChar(slotIndex);
          const rotation = text.getRotationOfChar(slotIndex);
          const icon = document.createElementNS(SVG_NAMESPACE, 'rect');

          icon.setAttribute('class', 'engraving-ring__icon');
          icon.setAttribute('x', -iconSize / 2);
          icon.setAttribute('y', -iconSize / 2);
          icon.setAttribute('width', iconSize);
          icon.setAttribute('height', iconSize);
          icon.setAttribute('mask', `url(#${this.iconMasks.get(node.dataset.icon)})`);
          // The glyph positions are on the baseline; the icon goes up to the middle of the text, like the letters
          const angle = (rotation * Math.PI) / 180;
          const x = (start.x + end.x) / 2 + Math.sin(angle) * inkCenter;
          const y = (start.y + end.y) / 2 - Math.cos(angle) * inkCenter;
          icon.setAttribute('transform', `translate(${x} ${y}) rotate(${rotation})`);
          iconLayer.append(icon);
        }

        index += node.textContent.length;
      });
    } catch (error) {
      // The text can't be measured while it is not displayed; it is drawn again on the next update
    }
  }

  // Centers the visible ink of the text on the middle of its curve. Browsers place text by the font's metrics,
  // which differ between Safari and Chrome and don't match what script fonts actually draw, so the ink is measured
  // and the text is moved along the curve (startOffset) and across it (dy). The icons count as ink too: the canvas
  // only sees their slots as spaces, so their place is added from the slot widths. Returns the height of the ink
  // center above the baseline.
  centerOnPath(text, textPath) {
    const path = document.getElementById(textPath.getAttribute('href').slice(1));
    const content = textPath.textContent;
    const hasIcons = Array.from(textPath.children).some((node) => node.dataset.icon);

    if (!path || (content.trim() === '' && !hasIcons)) {
      textPath.setAttribute('startOffset', '50%');
      text.removeAttribute('dy');
      return 0;
    }

    this.measureContext ??= document.createElement('canvas').getContext('2d');
    const context = this.measureContext;
    context.font = `${text.style.fontSize} ${text.style.fontFamily || 'serif'}`;
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';

    // Horizontal ink, measured from the middle of the text
    const metrics = context.measureText(content);
    const hasLetters = content.trim() !== '';
    let inkLeft = hasLetters ? -metrics.actualBoundingBoxLeft : Infinity;
    let inkRight = hasLetters ? metrics.actualBoundingBoxRight : -Infinity;

    const iconSize = parseFloat(text.style.fontSize) * ICON_SCALE;
    let advance = 0;

    textPath.childNodes.forEach((node) => {
      const part = node.textContent;

      if (node.nodeType === Node.ELEMENT_NODE && node.dataset.icon) {
        const iconAt = Number(node.dataset.iconAt);
        const before = context.measureText(part.slice(0, iconAt)).width;
        const middle = context.measureText(part.charAt(iconAt)).width;
        const iconCenter = advance + before + middle / 2 - metrics.width / 2;
        inkLeft = Math.min(inkLeft, iconCenter - iconSize / 2);
        inkRight = Math.max(inkRight, iconCenter + iconSize / 2);
      }

      advance += context.measureText(part).width;
    });

    const inkCenterX = (inkLeft + inkRight) / 2;

    // Vertical ink: the letters, or a capital letter when there are only icons
    const letterMetrics = hasLetters ? metrics : context.measureText('H');
    const inkCenterY = (letterMetrics.actualBoundingBoxAscent - letterMetrics.actualBoundingBoxDescent) / 2;

    textPath.setAttribute('startOffset', path.getTotalLength() / 2 - inkCenterX);
    text.setAttribute('dy', inkCenterY);

    return inkCenterY;
  }

  onModalInput(event) {
    const field = event.target.closest('[data-engraving-field]');
    if (!field) return;

    const name = field.dataset.engravingField;
    const value = this.sanitize(field.value);
    const caret = field.selectionStart ?? value.length;

    if (this.countCharacters(value) > Number(field.dataset.maxLength)) {
      // Too long: keep the previous value
      const previous = this.lastValues[name] ?? '';
      const removedLength = field.value.length - previous.length;
      field.value = previous;
      field.setSelectionRange(Math.max(0, caret - removedLength), Math.max(0, caret - removedLength));
      this.flashCounter(name);
    } else if (value !== field.value) {
      const removedLength = field.value.length - value.length;
      field.value = value;
      field.setSelectionRange(Math.max(0, caret - removedLength), Math.max(0, caret - removedLength));
    }

    this.lastValues[name] = field.value;
    this.clearErrors(name);
    this.updateDraft();
  }

  onModalChange(event) {
    if (event.target.matches('[data-engraving-surface], [data-engraving-font]')) {
      this.clearErrors();
      this.updateDraft();
    }
  }

  // Backspace and Delete remove a whole icon token
  onModalKeydown(event) {
    const field = event.target.closest('[data-engraving-field]');
    if (!field || (event.key !== 'Backspace' && event.key !== 'Delete')) return;
    if (field.selectionStart !== field.selectionEnd) return;

    const caret = field.selectionStart;
    const value = field.value;
    let start = -1;
    let end = -1;

    if (event.key === 'Backspace' && value[caret - 1] === ']') {
      start = value.lastIndexOf('[', caret - 1);
      end = caret;
    } else if (event.key === 'Delete' && value[caret] === '[') {
      start = caret;
      end = value.indexOf(']', caret) + 1;
    }

    if (start === -1 || end <= start || !this.isToken(value.slice(start, end))) return;

    event.preventDefault();
    field.value = value.slice(0, start) + value.slice(end);
    field.setSelectionRange(start, start);
    field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onModalClick(event) {
    const iconButton = event.target.closest('[data-engraving-icon]');

    if (iconButton) {
      this.insertIcon(iconButton.dataset.engravingIcon);
    } else if (event.target.closest('[data-engraving-save]')) {
      this.save();
    } else if (event.target.closest('[data-engraving-clear]')) {
      this.clearDraft();
    }
  }

  insertIcon(name) {
    let field = this.activeField;

    if (!field || !this.isFieldActive(field.dataset.engravingField)) {
      field = this.fields.find((candidate) => this.isFieldActive(candidate.dataset.engravingField));
    }

    if (!field) return;

    const token = `[${name}]`;

    if (this.countCharacters(field.value) + 1 > Number(field.dataset.maxLength)) {
      this.flashCounter(field.dataset.engravingField);
      return;
    }

    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? field.value.length;

    field.value = field.value.slice(0, start) + token + field.value.slice(end);
    field.setSelectionRange(start + token.length, start + token.length);
    this.activeField = field;
    field.dispatchEvent(new Event('input', { bubbles: true }));

    // On touch screens, focusing would open the keyboard on every icon tap
    if (window.matchMedia('(hover: hover)').matches) {
      field.focus();
    }
  }

  // Empties the popup and puts the options back to their defaults. The saved engraving only changes on "Save"
  clearDraft() {
    this.surfaceRadios.forEach((radio) => (radio.checked = radio.value === this.defaults.surface));
    this.fontRadios.forEach((radio) => (radio.checked = radio.value === this.defaults.font));

    this.fields.forEach((field) => {
      field.value = '';
      this.lastValues[field.dataset.engravingField] = '';
    });

    this.activeField = null;
    this.clearErrors();
    this.updateDraft();
  }

  save() {
    const values = {};
    const emptyFields = [];

    this.fields.forEach((field) => {
      const name = field.dataset.engravingField;
      values[name] = this.isFieldActive(name) ? this.tidy(field.value) : '';

      if (this.isFieldActive(name) && values[name] === '') {
        emptyFields.push(name);
      }
    });

    const hasEngraving = Object.values(values).some((value) => value !== '');

    // "Inside & Outside" needs both texts
    if (hasEngraving && emptyFields.length > 0) {
      emptyFields.forEach((name) => this.showError(name, this.saveButton.dataset.errorEmpty));
      this.modal.querySelector(`[data-engraving-field="${emptyFields[0]}"]`)?.focus();
      return;
    }

    if (hasEngraving) {
      this.saved = { surface: this.draftSurface, inside: values.inside ?? '', outside: values.outside ?? '', font: this.draftFont?.value ?? null };
    } else {
      this.saved = { surface: this.defaults.surface, inside: '', outside: '', font: this.defaults.font };
    }

    this.renderSaved();
    this.modal.hide();
  }

  remove() {
    this.saved = { surface: this.defaults.surface, inside: '', outside: '', font: this.defaults.font };
    this.renderSaved();
    this.querySelector('[data-engraving-add]')?.focus();
  }

  /* ---------- Saved engraving (product page) ---------- */

  get hasSavedEngraving() {
    return this.saved.inside !== '' || this.saved.outside !== '';
  }

  renderSaved() {
    const hasEngraving = this.hasSavedEngraving;
    const surfaceRadio = this.surfaceRadios.find((radio) => radio.value === this.saved.surface);
    const fontRadio = this.fontRadios.find((radio) => radio.value === this.saved.font);

    const propertyValues = {
      surface: hasEngraving && surfaceRadio ? surfaceRadio.dataset.text : '',
      inside: this.saved.inside,
      outside: this.saved.outside,
      font: hasEngraving && fontRadio && this.usesSelectedFont(this.saved) ? fontRadio.value : ''
    };

    this.querySelectorAll('[data-engraving-property]').forEach((input) => {
      const value = propertyValues[input.dataset.engravingProperty] ?? '';
      input.value = value;
      input.disabled = value === '';
    });

    this.querySelector('[data-engraving-add-area]').hidden = hasEngraving;

    const summary = this.querySelector('[data-engraving-summary]');
    const list = this.querySelector('[data-engraving-summary-list]');
    summary.hidden = !hasEngraving;
    list.replaceChildren();

    if (!hasEngraving) return;

    this.fields.forEach((field) => {
      const name = field.dataset.engravingField;
      if (this.saved[name] === '') return;

      const label = this.modal.querySelector(`label[for="${field.id}"]`)?.textContent.trim() ?? name;
      const item = document.createElement('li');
      const labelElement = document.createElement('span');
      const textElement = document.createElement('span');

      labelElement.className = 'engraving__summary-label';
      labelElement.textContent = `${label}:`;
      textElement.className = 'engraving__summary-text';
      const family = this.fontFamilyFor(name, fontRadio?.dataset.fontFamily);
      if (family) textElement.style.fontFamily = `"${family}", serif`;
      this.renderText(textElement, this.saved[name]);

      item.append(labelElement, ' ', textElement);
      list.append(item);
    });

    if (fontRadio && this.fontRadios.length > 1 && this.usesSelectedFont(this.saved)) {
      const item = document.createElement('li');
      const fontLabel = this.modal.querySelector('[data-engraving-font-label]')?.textContent.trim();
      item.className = 'engraving__summary-font';
      item.textContent = `${fontLabel || 'Font'}: ${fontRadio.value}`;
      list.append(item);
    }
  }

  /* ---------- Help tooltip next to the button (same behavior as the line item property tooltip) ---------- */

  setupHelp() {
    // Collected before the modal is moved to <body>, so the tooltip of the popup title is included
    this.helps = Array.from(this.querySelectorAll('[data-engraving-help]'));
    if (this.helps.length === 0) return;

    this.helps.forEach((help) => {
      const toggle = help.querySelector('.engraving__help-toggle');
      const content = help.querySelector('.engraving__help-content');

      // Tap or click toggles it
      toggle.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        this.closeHelp(help);
        if (!isOpen) this.positionHelp(help);
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });

      // Mouse: opens on the icon, stays open while moving to the tooltip and closes shortly after leaving both
      help.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(help.closeTimer);
        this.positionHelp(help);
        help.setAttribute('data-help-hover', '');
      });

      help.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        help.closeTimer = setTimeout(() => help.removeAttribute('data-help-hover'), 400);
      });

      toggle.addEventListener('focus', () => this.positionHelp(help));

      // A link to "#chat" opens the Shopify Inbox chat when it is available, otherwise it follows its URL
      content.addEventListener('click', (event) => {
        const chatLink = event.target.closest('a[href*="#chat"]');
        const chat = document.querySelector('shopify-chat');

        if (chatLink && chat && typeof chat.show === 'function') {
          event.preventDefault();
          this.closeHelp();
          chat.show();
        }
      });
    });

    document.addEventListener('click', (event) => {
      this.helps.forEach((help) => {
        if (!help.contains(event.target)) this.closeHelp(null, help);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeHelp();
    });
  }

  // Closes every tooltip (or only "only"), except "except"
  closeHelp(except = null, only = null) {
    (only ? [only] : this.helps ?? []).forEach((help) => {
      if (help === except) return;
      help.querySelector('.engraving__help-toggle')?.setAttribute('aria-expanded', 'false');
      help.removeAttribute('data-help-hover');
    });
  }

  // Keeps the tooltip inside the screen; the arrow keeps pointing at the icon
  positionHelp(help) {
    const content = help.querySelector('.engraving__help-content');
    content.style.removeProperty('--help-shift');

    const gutter = 16;
    const box = content.getBoundingClientRect();
    let shift = 0;

    if (box.left < gutter) {
      shift = gutter - box.left;
    } else if (box.right > document.documentElement.clientWidth - gutter) {
      shift = document.documentElement.clientWidth - gutter - box.right;
    }

    if (shift !== 0) content.style.setProperty('--help-shift', `${shift}px`);
  }

  /* ---------- Helpers ---------- */

  isToken(text) {
    return /^\[[^[\]]+\]$/.test(text) && this.icons.has(text.slice(1, -1));
  }

  // Keeps English letters only (accented ones are converted) and removes what the laser can't engrave, keeping icon tokens
  sanitize(value) {
    return value
      .split(TOKEN_SPLIT)
      .map((part) => (this.isToken(part) ? part : toEnglishLetters(part).replace(DISALLOWED_CHARACTERS, '')))
      .join('');
  }

  // Trims and collapses spaces before saving
  tidy(value) {
    return this.sanitize(value).replace(/\s+/g, ' ').trim();
  }

  countCharacters(value) {
    return value
      .split(TOKEN_SPLIT)
      .reduce((total, part) => total + (this.isToken(part) ? 1 : Array.from(part).length), 0);
  }

  // Writes the text into the element, drawing icon tokens as icons
  renderText(element, value, placeholder = '') {
    element.replaceChildren();
    element.classList.toggle('is-empty', value === '');

    if (value === '') {
      if (placeholder) element.textContent = placeholder;
      return;
    }

    value.split(TOKEN_SPLIT).forEach((part) => {
      if (part === '') return;

      if (this.isToken(part)) {
        const name = part.slice(1, -1);
        const icon = document.createElement('span');
        icon.className = 'engraving-icon';
        icon.setAttribute('role', 'img');
        icon.setAttribute('aria-label', name);
        icon.style.setProperty('--engraving-icon', `url("${this.icons.get(name)}")`);
        element.append(icon);
      } else {
        element.append(part);
      }
    });
  }

  flashCounter(name) {
    const counter = this.modal.querySelector(`[data-engraving-counter="${name}"]`);
    if (!counter) return;

    counter.classList.remove('is-limit');
    void counter.offsetWidth; // Restarts the animation
    counter.classList.add('is-limit');
  }

  showError(name, message) {
    const error = this.modal.querySelector(`[data-engraving-error="${name}"]`);
    if (!error) return;

    error.textContent = message;
    error.hidden = false;
    this.modal.querySelector(`[data-engraving-field="${name}"]`)?.setAttribute('aria-invalid', 'true');
  }

  clearErrors(name = null) {
    this.modal.querySelectorAll(name ? `[data-engraving-error="${name}"]` : '[data-engraving-error]').forEach((error) => {
      error.hidden = true;
      error.textContent = '';
    });

    this.modal.querySelectorAll(name ? `[data-engraving-field="${name}"]` : '[data-engraving-field]').forEach((field) => {
      field.removeAttribute('aria-invalid');
    });
  }
}

if (!window.customElements.get('engraving-picker')) {
  window.customElements.define('engraving-picker', EngravingPicker);
}
